import { compareOrderHints, compareStringOrdinal } from "./Comparators";
/**
 * Creates an expression used by the service to calculate a new order hint based on lowOrderHint and highOrderhint.
 *
 * for null, null => orderhint should be " !"
 * for A, null => orderhint should be "A !"
 * for null, B => orderhint should be " B!"
 * for A, B => orderhint should be "A B!"
 * @param lowOrderHint Smaller order hint
 * @param highOrderHint Bigger order hint
 */ export function generateOrderHintBetween(lowOrderHint, highOrderHint) {
    if (lowOrderHint && highOrderHint && compareStringOrdinal(lowOrderHint, highOrderHint) > 0) {
        throw new Error("ArgumentException: lowOrderHint cannot be higher than highOrderHint");
    }
    const lowValue = lowOrderHint || "";
    const highValue = highOrderHint || "";
    return lowValue + " " + highValue + "!";
}
/**
 * Reconciles a *resolved* order hint back into a positioning *expression* ("<low> <high>!") that lands
 * the target in the slot its resolved hint describes, relative to a set of sibling hints.
 *
 * The service persists resolved order hints, but the update API only accepts positioning expressions -
 * a bare resolved value is rejected. When the caller has just a resolved hint (e.g. an undo/redo
 * reversal restores the pre-move resolved value via an inverse delta), this recovers the tightest
 * live neighbors bracketing that value and generates an equivalent expression. Re-bracketing against
 * the *current* siblings keeps the result correct even if neighbors shifted since the original move.
 *
 * @param targetHint The resolved order hint describing the desired slot.
 * @param siblingHints The order hints of the other entities the target is positioned among. The
 * target's own hint should be excluded by the caller.
 * @returns A positioning expression equivalent to the target slot.
 */ export function reconcileOrderHintExpression(targetHint, siblingHints) {
    // Find the tightest neighbors bracketing the target: high and low. The
    // generated expression resolves to a value between them, reproducing the target's slot.
    let low = null;
    let high = null;
    for (const siblingHint of siblingHints){
        if (siblingHint == null) {
            continue;
        }
        if (compareOrderHints(siblingHint, targetHint) < 0) {
            if (low == null || compareOrderHints(siblingHint, low) > 0) {
                low = siblingHint;
            }
        } else if (compareOrderHints(siblingHint, targetHint) > 0) {
            if (high == null || compareOrderHints(siblingHint, high) < 0) {
                high = siblingHint;
            }
        }
    }
    return generateOrderHintBetween(low, high);
}
